import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import {
  InvalidateQueryFilters,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { SignalAPI } from 'api/signal';
import {
  CreateSignalPayload,
  GetSignalConfigurationFromPayload,
  SignalConfiguration,
} from 'api/signal/types';
import { useCallback } from 'react';

const SIGNAL_CONFIGURATION_BASE_KEY = 'signal-configurations';
const SIGNAL_CONFIGURATIONS_KEY = [SIGNAL_CONFIGURATION_BASE_KEY];

export const useGetAllSignalConfigurationsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<
      SignalConfiguration[],
      Error,
      SignalConfiguration[],
      typeof SIGNAL_CONFIGURATIONS_KEY
    >,
    'queryKey'
  >,
): UseQueryResult<SignalConfiguration[], any> =>
  useQuery({
    queryKey: SIGNAL_CONFIGURATIONS_KEY,
    queryFn: () => SignalAPI.getAllSignalConfigurations(),
    ...queryOptions,
  });
type SignalConfigurationQueryKeyType = [
  typeof SIGNAL_CONFIGURATION_BASE_KEY,
  number,
];
const getSignalConfigurationQueryKey = (
  signalId: number,
): SignalConfigurationQueryKeyType =>
  [SIGNAL_CONFIGURATION_BASE_KEY, signalId] as const;

export const useGetSignalConfigurationQuery = (
  signalId: number,
  queryOptions?: Omit<
    UseQueryOptions<
      SignalConfiguration,
      Error,
      SignalConfiguration,
      SignalConfigurationQueryKeyType
    >,
    'queryKey'
  >,
) =>
  useQuery({
    queryKey: getSignalConfigurationQueryKey(signalId),
    queryFn: () => SignalAPI.getSignalConfiguration(signalId),
    ...queryOptions,
  });

type SignalConfigurationAllDetailsQueryKeyType = [
  typeof SIGNAL_CONFIGURATION_BASE_KEY,
  number,
  'all-details',
];
const getSignalConfigurationAllDetailsQueryKey = (
  signalId: number,
): SignalConfigurationAllDetailsQueryKeyType =>
  [SIGNAL_CONFIGURATION_BASE_KEY, signalId, 'all-details'] as const;

export const useGetSignalConfigurationAllDetailsQuery = (
  signalId: number,
  queryOptions?: Omit<
    UseQueryOptions<any, Error, any, SignalConfigurationAllDetailsQueryKeyType>,
    'queryKey'
  >,
) =>
  useQuery({
    queryKey: getSignalConfigurationAllDetailsQueryKey(signalId),
    queryFn: () => SignalAPI.getSignalConfigurationAllDetails(signalId),
    ...queryOptions,
  });
export const temporarySignalConfigurationId = -1;
const TEMPORARY_SIGNAL_CONFIGURATION_KEY = [
  SIGNAL_CONFIGURATION_BASE_KEY,
  'new',
];

export const useCreateTemporarySignalConfigurationMutation = (
  options: UseMutationOptions<
    SignalConfiguration,
    Error,
    GetSignalConfigurationFromPayload
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: GetSignalConfigurationFromPayload) => {
      try {
        const response = await SignalAPI.getSignalConfigurationPrompt(payload);
        const signalResponseWithId = {
          ...response,
          signalConfigurationId: temporarySignalConfigurationId,
        };

        queryClient.setQueryDefaults(TEMPORARY_SIGNAL_CONFIGURATION_KEY, {
          staleTime: Infinity,
          gcTime: Infinity,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        });
        queryClient.setQueryData<SignalConfiguration>(
          TEMPORARY_SIGNAL_CONFIGURATION_KEY,
          signalResponseWithId,
        );

        return Promise.resolve(signalResponseWithId);
      } catch (error) {
        throw new Error('Failed to create temporary signal configuration', {
          cause: error,
        });
      }
    },
    ...options,
  });
};
export const useGetTemporarySignalConfiguration = () => {
  const queryClient = useQueryClient();

  return queryClient.getQueryData<SignalConfiguration>(
    TEMPORARY_SIGNAL_CONFIGURATION_KEY,
  );
};

const useInvalidateAllSignalConfigurationsQuery = (
  options?: InvalidateQueryFilters,
) => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: SIGNAL_CONFIGURATIONS_KEY,
      exact: true,
      ...options,
    });
  }, [queryClient, options]);
};
const useGetSuccessHandlerWithInvalidateConfigurationsQuery = <
  TData,
  TVariables,
>(
  options: UseMutationOptions<TData, Error, TVariables>,
) => {
  const invalidateAllSignalConfigurationsQuery =
    useInvalidateAllSignalConfigurationsQuery({
      refetchType: 'all',
    });

  return useCallback(
    async (data: TData, variables: TVariables, context: unknown) => {
      await invalidateAllSignalConfigurationsQuery();
      options.onSuccess?.(data, variables, context);
    },
    [invalidateAllSignalConfigurationsQuery, options],
  );
};

export const useInvalidateTemporarySignalConfigurationQuery = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.removeQueries({
      queryKey: TEMPORARY_SIGNAL_CONFIGURATION_KEY,
    });
  }, [queryClient]);
};

export const useCreateSignalConfigurationMutation = (
  options: UseMutationOptions<
    SignalConfiguration,
    Error,
    CreateSignalPayload
  > = {},
) => {
  const successHandler =
    useGetSuccessHandlerWithInvalidateConfigurationsQuery(options);

  return useMutation({
    mutationFn: (payload: CreateSignalPayload) => {
      return SignalAPI.createSignalConfigurations(payload);
    },
    onSuccess: async (data, variables, context) => {
      successHandler(data, variables, context);
    },
    ...options,
  });
};
export const useUpdateSignalConfigurationMutation = (
  options: UseMutationOptions<
    SignalConfiguration,
    Error,
    SignalConfiguration
  > = {},
) => {
  const queryClient = useQueryClient();
  const successHandler =
    useGetSuccessHandlerWithInvalidateConfigurationsQuery(options);

  return useMutation({
    mutationFn: (payload: SignalConfiguration) => {
      return SignalAPI.updateSignalConfigurations(payload);
    },
    onSuccess: async (data, variables, context) => {
      successHandler(data, variables, context);
      await queryClient.invalidateQueries({
        queryKey: getSignalConfigurationQueryKey(
          variables.signalConfigurationId,
        ),
      });
    },
    ...options,
  });
};

export const useDeleteSignalConfigurationMutation = (
  options: UseMutationOptions<void, Error, number> = {},
) => {
  const queryClient = useQueryClient();
  const successHandler =
    useGetSuccessHandlerWithInvalidateConfigurationsQuery(options);

  return useMutation({
    mutationFn: (signalConfigurationId: number) =>
      SignalAPI.deleteSignalConfiguration(signalConfigurationId),
    onSuccess: async (data, variables, context) => {
      queryClient.resetQueries({
        queryKey: getSignalConfigurationQueryKey(variables),
      });
      successHandler(data, variables, context);
    },
    ...options,
  });
};

type SuggestedFieldOptionsForSignalConfigurationKeyType = [
  'suggestion-options',
  number,
  'signal-configuration',
];
const SuggestedFieldOptionsForSignalConfigurationKey = (
  fieldId: number,
): SuggestedFieldOptionsForSignalConfigurationKeyType => [
  'suggestion-options',
  fieldId,
  'signal-configuration',
];

export const useGetSuggestedFieldOptionsForSignalConfigurationQuery = (
  fieldId: number,
  options: Omit<
    UseQueryOptions<
      SingleAndMultiSelectFieldOption[],
      Error,
      any[],
      SuggestedFieldOptionsForSignalConfigurationKeyType
    >,
    'queryKey'
  > = {},
): UseQueryResult<SingleAndMultiSelectFieldOption[], any> => {
  return useQuery({
    queryKey: SuggestedFieldOptionsForSignalConfigurationKey(fieldId),
    queryFn: () => [] as any,
    ...options,
  });
};
